"use client"

import { Component } from "react"
import { Link } from "react-router-dom"
import { connect } from "react-redux"
import SearchBox from "../../../components/common/SearchBox"
import WeeklyItem from "../../../components/common/WeeklyItem"
import EmptyState from "../../../components/common/EmptyState"
import BoardLoading from "../../../components/common/BoardLoading"
import actionTypes from "../../../constants/actionTypes"
import "../../../resources/scss/weekly.scss"

class Weekly extends Component {
    constructor(props) {
        super(props)
        this.state = {
            currentMonth: new Date().getMonth(),
            currentYear: new Date().getFullYear(),
            searchTerm: "",
            currentPage: 1,
            weeklyPerPage: 10,
            isSearching: false,
            showDropdown: {},
            // 모달 관련 상태 추가
            showModal: false,
            selectedWeekly: null,
            selectedWeeklyId: null,
            // 이미지 확대 모달 상태
            showImageModal: false,
            selectedImageIndex: 0,
            imageZoom: 1,
        }

        // 검색 디바운싱을 위한 타이머
        this.searchTimer = null
        // 주보 메뉴 ID
        this.WEEKLY_MENU_ID = 16
    }

    componentDidMount() {
        this.fetchWeeklyData()
        // ESC 키 이벤트 리스너 추가
        document.addEventListener("keydown", this.handleKeyDown)
    }

    componentWillUnmount() {
        // 컴포넌트 언마운트 시 이벤트 리스너 제거
        document.removeEventListener("keydown", this.handleKeyDown)
        // 상세 정보 초기화
        this.props.dispatch({
            type: actionTypes.BOARD_DETAIL_RESET,
        })
    }

    componentDidUpdate(prevProps, prevState) {
        // 페이지, 월, 년도가 변경되었을 때만 API 호출
        if (
            prevState.currentPage !== this.state.currentPage ||
            prevState.currentMonth !== this.state.currentMonth ||
            prevState.currentYear !== this.state.currentYear
        ) {
            this.fetchWeeklyData()
        }

        // 검색어가 변경되었을 때는 디바운싱 적용
        if (prevState.searchTerm !== this.state.searchTerm) {
            this.handleSearchDebounce()
        }

        // 상세 정보가 로드되었을 때 모달 표시
        const { board } = this.props
        const detailData = board?.detail?.data
        const detailLoading = board?.detail?.loading
        const detailError = board?.detail?.error

        // 상세 정보 로드 완료 시 모달 열기
        if (!detailLoading && !detailError && detailData && this.state.selectedWeeklyId && !this.state.showModal) {
            // 상세 데이터에 원본 ID 추가
            const enhancedData = {
                ...detailData,
                originalId: this.state.selectedWeeklyId, // 상세 조회 시 사용한 원본 ID 저장
            }

            this.setState({
                showModal: true,
                selectedWeekly: enhancedData,
            })
        }

        // 상세 정보 로드 실패 시 처리
        if (!detailLoading && detailError && this.state.selectedWeeklyId) {
            console.error("주보 상세 정보 로드 실패:", detailError)
            alert("주보 상세 정보를 불러오는데 실패했습니다.")
            this.setState({
                selectedWeeklyId: null,
            })
        }

        // 삭제 성공/실패 처리 추가
        const deleteState = board?.delete

        if (deleteState && !prevProps.board?.delete?.success && deleteState.success) {
            alert("주보가 성공적으로 삭제되었습니다.")
            // 목록 새로고침
            this.fetchWeeklyData()
        }

        if (deleteState && !prevProps.board?.delete?.error && deleteState.error) {
            console.error("주보 삭제 실패:", deleteState.error)
            alert(`삭제 실패: ${deleteState.error}`)
        }
    }

    // ESC 키 이벤트 핸들러
    handleKeyDown = (e) => {
        if (e.key === "Escape") {
            if (this.state.showImageModal) {
                this.closeImageModal()
            } else if (this.state.showModal) {
                this.closeModal()
            }
        }
        // 이미지 모달에서 방향키 네비게이션
        if (this.state.showImageModal) {
            if (e.key === "ArrowLeft") {
                this.previousImage()
            } else if (e.key === "ArrowRight") {
                this.nextImage()
            }
        }
    }

    // 검색어 디바운싱 처리
    handleSearchDebounce = () => {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer)
        }

        this.setState({ isSearching: true })

        this.searchTimer = setTimeout(() => {
            this.fetchWeeklyData()
            this.setState({ isSearching: false })
        }, 500) // 500ms 디바운싱
    }

    // 날짜 문자열을 Date 객체로 변환하는 헬퍼 함수
    parseDate = (dateString) => {
        if (!dateString) return null
        try {
            // ISO 형식 (2025-06-15T17:28:45) 또는 다른 형식 처리
            return new Date(dateString)
        } catch (error) {
            console.warn("날짜 파싱 실패:", dateString, error)
            return null
        }
    }

    // 클라이언트 사이드 날짜 필터링
    filterBulletinsByDate = (bulletins, targetYear, targetMonth) => {
        if (!bulletins || bulletins.length === 0) return []

        const filtered = bulletins.filter((bulletin) => {
            // 다양한 날짜 필드 확인
            const dateString = bulletin.createdAt || bulletin.createdDate || bulletin.publishDate || bulletin.date

            if (!dateString) {
                console.warn("날짜 정보 없음:", bulletin.title)
                return false
            }

            const bulletinDate = this.parseDate(dateString)
            if (!bulletinDate || isNaN(bulletinDate.getTime())) {
                console.warn("유효하지 않은 날짜:", dateString, bulletin.title)
                return false
            }

            const bulletinYear = bulletinDate.getFullYear()
            const bulletinMonth = bulletinDate.getMonth() // 0-based

            const matches = bulletinYear === targetYear && bulletinMonth === targetMonth

            return matches
        })

        return filtered
    }

    fetchWeeklyData = () => {
        const { currentPage, weeklyPerPage, searchTerm } = this.state


        // 기본 파라미터 (모든 데이터 조회)
        const params = {
            menuId: this.WEEKLY_MENU_ID,
            page: 0, // 모든 데이터를 가져오기 위해 첫 페이지부터
            size: 1000, // 충분히 큰 사이즈로 모든 데이터 조회
        }

        // 검색어가 있으면 검색 파라미터 추가
        if (searchTerm.trim()) {
            params.search = searchTerm.trim()
            params.keyword = searchTerm.trim()
            params.title = searchTerm.trim()
            params.q = searchTerm.trim()
        } else {
        }

        console.table(params)

        // Redux 액션 디스패치
        this.props.dispatch({
            type: actionTypes.BOARD_FETCH,
            payload: params,
        })
    }

    // 관리자 권한 체크 함수 (수정)
    hasAdminPermission = () => {
        const { loginData } = this.props


        if (!loginData) {
            return false
        }

        const { isLogin, role, user } = loginData

        // isLogin이 명시적으로 true이고 role이 있는 경우만 허용
        if (isLogin === true && role) {
            const adminRoles = ["ROLE_WEB_ADMIN", "ROLE_ADMIN"]
            const hasPermission = adminRoles.includes(role)
            return hasPermission
        }

        return false
    }

    // 이전 달로 이동
    goToPreviousMonth = () => {
        this.setState((prevState) => {
            const newState =
                prevState.currentMonth === 0
                    ? {
                        currentMonth: 11,
                        currentYear: prevState.currentYear - 1,
                        currentPage: 1,
                        searchTerm: "", // 월 변경 시 검색어 초기화
                    }
                    : {
                        currentMonth: prevState.currentMonth - 1,
                        currentPage: 1,
                        searchTerm: "", // 월 변경 시 검색어 초기화
                    }
            return newState
        })
    }

    // 다음 달로 이동
    goToNextMonth = () => {
        this.setState((prevState) => {
            const newState =
                prevState.currentMonth === 11
                    ? {
                        currentMonth: 0,
                        currentYear: prevState.currentYear + 1,
                        currentPage: 1,
                        searchTerm: "", // 월 변경 시 검색어 초기화
                    }
                    : {
                        currentMonth: prevState.currentMonth + 1,
                        currentPage: 1,
                        searchTerm: "", // 월 변경 시 검색어 초기화
                    }

            return newState
        })
    }

    // 월 이름 가져오기
    getMonthName = (month) => {
        const monthNames = ["1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월"]
        return monthNames[month]
    }

    handleSearchChange = (e) => {
        this.setState({
            searchTerm: e.target.value,
            currentPage: 1,
        })
    }

    // 검색어 초기화
    clearSearch = () => {
        this.setState(
            {
                searchTerm: "",
                currentPage: 1,
            },
            () => {
                this.fetchWeeklyData()
            },
        )
    }

    // 주보 클릭 핸들러 - 상세 정보 API 호출
    handleWeeklyClick = (item, index) => {

        // ID 필드 우선순위: id > boardId > contentId > _id
        const itemId = item.id || item.boardId || item.contentId || item._id

        if (!itemId) {
            console.error("❌ 게시물 ID가 없습니다:", item)
            alert("게시물 정보가 올바르지 않습니다.")
            return
        }

        // 상세 정보 초기화
        this.props.dispatch({
            type: actionTypes.BOARD_DETAIL_RESET,
        })

        // 선택된 주보 ID 저장 (원본 ID 보관)
        this.setState({
            selectedWeeklyId: itemId,
            selectedWeekly: null,
            showModal: false,
        })

        // 상세 정보 조회 API 호출
        this.props.dispatch({
            type: actionTypes.BOARD_DETAIL_FETCH,
            payload: {
                id: itemId,
            },
        })
    }

    // 드롭다운 토글
    toggleDropdown = (weeklyId) => {
        this.setState((prevState) => ({
            showDropdown: {
                ...prevState.showDropdown,
                [weeklyId]: !prevState.showDropdown[weeklyId],
            },
        }))
    }

    // 주보 수정
    handleEditWeekly = (item, e) => {
        e.stopPropagation()

        // 다양한 ID 필드 확인
        const itemId = item.id || item.boardId || item.contentId || item._id

        if (!itemId) {
            alert("주보 정보를 찾을 수 없습니다.")
            return
        }
        window.location.href = `/weekly/edit/${itemId}`
        this.setState({ showDropdown: {} })
    }

    // 주보 삭제 (더 강력한 디버깅)
    handleDeleteWeekly = (item, e) => {
        e.stopPropagation()

        console.table(item)

        // 원본 ID 우선 사용
        const originalId = item.originalId || item.id || item.boardId || item.contentId || item._id

        if (!originalId) {
            console.error("❌ 원본 ID를 찾을 수 없습니다!")
            alert("주보 정보를 찾을 수 없습니다.")
            return
        }

        const numericId = Number(originalId)
        if (isNaN(numericId)) {
            console.error("❌ 원본 ID가 숫자가 아닙니다:", originalId)
            alert("유효하지 않은 주보 ID입니다.")
            return
        }


        if (window.confirm(`"${item.title}" 주보를 삭제하시겠습니까?`)) {

            this.props.dispatch({
                type: actionTypes.BOARD_DELETE,
                payload: {
                    boardId: numericId,
                },
            })
            this.setState({ showDropdown: {} })

            setTimeout(() => {
                this.fetchWeeklyData()
            }, 1000)
        }
    }

    // 모달 닫기
    closeModal = () => {
        this.setState({
            showModal: false,
            selectedWeekly: null,
            selectedWeeklyId: null,
        })

        // 상세 정보 초기화
        this.props.dispatch({
            type: actionTypes.BOARD_DETAIL_RESET,
        })
    }

    // 이미지 확대 모달 열기
    openImageModal = (imageIndex) => {
        this.setState({
            showImageModal: true,
            selectedImageIndex: imageIndex,
            imageZoom: 1,
        })
    }

    // 이미지 확대 모달 닫기
    closeImageModal = () => {
        this.setState({
            showImageModal: false,
            selectedImageIndex: 0,
            imageZoom: 1,
        })
    }

    // 이전 이미지
    previousImage = () => {
        const images = this.extractAllImages(this.state.selectedWeekly.contents)
        this.setState((prevState) => ({
            selectedImageIndex: prevState.selectedImageIndex > 0 ? prevState.selectedImageIndex - 1 : images.length - 1,
            imageZoom: 1,
        }))
    }

    // 다음 이미지
    nextImage = () => {
        const images = this.extractAllImages(this.state.selectedWeekly.contents)
        this.setState((prevState) => ({
            selectedImageIndex: prevState.selectedImageIndex < images.length - 1 ? prevState.selectedImageIndex + 1 : 0,
            imageZoom: 1,
        }))
    }

    // 이미지 줌 인
    zoomIn = () => {
        this.setState((prevState) => ({
            imageZoom: Math.min(prevState.imageZoom + 0.5, 3),
        }))
    }

    // 이미지 줌 아웃
    zoomOut = () => {
        this.setState((prevState) => ({
            imageZoom: Math.max(prevState.imageZoom - 0.5, 0.5),
        }))
    }

    // 줌 리셋
    resetZoom = () => {
        this.setState({ imageZoom: 1 })
    }

    // 주보 다운로드
    downloadWeekly = (item) => {
        if (item.contents) {
            // 이미지 다운로드 로직
            const images = this.extractAllImages(item.contents)
            if (images.length > 0) {
                images.forEach((imgSrc, index) => {
                    const link = document.createElement("a")
                    link.href = imgSrc
                    link.download = `${item.title}_${index === 0 ? "앞면" : "뒤면"}.jpg`
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                })
            }
        }
    }

    // 주보 면 구분 함수
    getPageLabel = (index, totalImages) => {
        if (totalImages === 1) return "주보"
        if (index === 0) return "앞면"
        if (index === 1) return "뒤면"
        return `${index + 1}면`
    }

    // 모달에서 수정 버튼 클릭 핸들러
    handleModalEdit = () => {
        const selectedWeekly = this.state.selectedWeekly
        const itemId = selectedWeekly.id || selectedWeekly.contentId || selectedWeekly.boardId || selectedWeekly._id

        this.closeModal()
        window.location.href = `/weekly/edit/${itemId}`
    }

    // 모달에서 삭제 버튼 클릭 핸들러 (강화된 디버깅)
    handleModalDelete = () => {
        const selectedWeekly = this.state.selectedWeekly


        // 원본 ID 우선 사용
        const originalId = selectedWeekly.originalId || this.state.selectedWeeklyId

        if (!originalId) {
            console.error("❌ 원본 ID를 찾을 수 없습니다!")
            alert("주보 정보를 찾을 수 없습니다.")
            return
        }

        const numericId = Number(originalId)
        if (isNaN(numericId)) {
            console.error("❌ 원본 ID가 숫자가 아닙니다:", originalId)
            alert("유효하지 않은 주보 ID입니다.")
            return
        }


        if (window.confirm(`"${selectedWeekly.title}" 주보를 삭제하시겠습니까?`)) {
            console.warn("⚠️ 원본 ID로 삭제 요청:", numericId)

            this.props.dispatch({
                type: actionTypes.BOARD_DELETE,
                payload: {
                    boardId: numericId,
                },
            })
            this.closeModal()
            setTimeout(() => {
                this.fetchWeeklyData()
            }, 1000)
        }
    }

    // 각 주보 아이템에 원본 ID 저장 (개선)
    enhanceBulletinWithOriginalId = (bulletin, originalId) => {
        return {
            ...bulletin,
            originalId: originalId || bulletin.id || bulletin.boardId || bulletin.contentId || bulletin._id,
        }
    }

    render() {
        const { currentMonth, currentYear, searchTerm, isSearching, showDropdown } = this.state
        const { board } = this.props

        // 새로운 상태 구조에서 주보 데이터 가져오기
        const weeklyBoard = board?.boards?.[this.WEEKLY_MENU_ID] || {}
        const { loading = false, data = {}, error = null } = weeklyBoard

        // 상세 정보 상태
        const detailLoading = board?.detail?.loading || false
        const detailError = board?.detail?.error

        // 원본 데이터
        const allWeeklyBulletins = data?.content || []
        const totalElements = data?.totalElements || 0

        // 클라이언트 사이드 날짜 필터링 적용
        let weeklyBulletins = allWeeklyBulletins

        // 검색어가 없을 때만 날짜 필터링 적용
        if (!searchTerm.trim()) {
            weeklyBulletins = this.filterBulletinsByDate(allWeeklyBulletins, currentYear, currentMonth)
        }

        // 검색 중이거나 API 로딩 중인 경우
        const isLoading = loading || isSearching

        if (isLoading) {
            return (
                <section className="weekly-page">
                    <div className="layout">
                        <div className="weekly-controls">
                            <div className="month-selector">
                                <button disabled className="month-nav" aria-label="이전 달">
                                    <i className="fas fa-chevron-left"></i>
                                </button>
                                <h3>
                                    {currentYear}년 {this.getMonthName(currentMonth)}
                                </h3>
                                <button disabled className="month-nav" aria-label="다음 달">
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        </div>

                        <BoardLoading
                            title={isSearching ? "주보 검색 중..." : "주보를 불러오는 중..."}
                            subtitle="잠시만 기다려주세요"
                            size="medium"
                        />
                    </div>
                </section>
            )
        }

        if (error) {
            console.error("=== ❌ API 오류 ===", error)
            return (
                <section className="weekly-page">
                    <div className="layout">
                        <EmptyState title="오류 발생" message="주보를 불러오는 중 오류가 발생했습니다." />
                    </div>
                </section>
            )
        }

        return (
            <section className="weekly-page">
                <div className="layout">
                    <div className="weekly-controls">
                        <div className="month-selector">
                            <button onClick={this.goToPreviousMonth} className="month-nav" aria-label="이전 달">
                                <i className="fas fa-chevron-left"></i>
                            </button>
                            <h3>
                                {currentYear}년 {this.getMonthName(currentMonth)}
                            </h3>
                            <button onClick={this.goToNextMonth} className="month-nav" aria-label="다음 달">
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        </div>

                        <div className="weekly-actions">

                            {/* 관리자만 주보 작성 버튼 표시 */}
                            {this.hasAdminPermission() && (
                                <Link to="/weekly/write" className="weekly-write-btn">
                                    <i className="fas fa-plus"></i>
                                    주보 작성
                                </Link>
                            )}
                        </div>
                    </div>

                    {weeklyBulletins.length > 0 ? (
                        <div className="weekly-grid">
                            {weeklyBulletins.map((bulletin, index) => {
                                // 원본 ID 추출 및 저장 (개선)
                                const originalId = bulletin.id || bulletin.contentId || bulletin.boardId || bulletin._id
                                const enhancedBulletin = this.enhanceBulletinWithOriginalId(bulletin, originalId)

                                return (
                                    <div key={originalId || `bulletin-${index}`} className="weekly-item-wrapper">
                                        <div onClick={() => this.handleWeeklyClick(enhancedBulletin, index)}>
                                            <WeeklyItem
                                                title={enhancedBulletin.title}
                                                date={enhancedBulletin.createdAt || enhancedBulletin.createdDate}
                                                thumbnail={this.extractFirstImage(enhancedBulletin.contents)}
                                                file={enhancedBulletin.contents}
                                            />
                                        </div>
                                        {/* 관리자만 관리 버튼 표시 */}
                                        {this.hasAdminPermission() && (
                                            <div className="weekly-item-actions">
                                                <button
                                                    className="action-btn"
                                                    onClick={() => this.toggleDropdown(originalId)}
                                                    title="관리 메뉴"
                                                >
                                                    <i className="fas fa-ellipsis-v"></i>
                                                </button>
                                                {showDropdown[originalId] && (
                                                    <div className="dropdown-menu">
                                                        <button
                                                            onClick={(e) => this.handleEditWeekly(enhancedBulletin, e)}
                                                            className="dropdown-item edit"
                                                        >
                                                            <i className="fas fa-edit"></i> 수정
                                                        </button>
                                                        <button
                                                            onClick={(e) => this.handleDeleteWeekly(enhancedBulletin, e)}
                                                            className="dropdown-item delete"
                                                        >
                                                            <i className="fas fa-trash"></i> 삭제
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <EmptyState
                            customMessage={searchTerm ? "검색 결과 없음" : "게시물 없음"}
                            customDescription={
                                searchTerm
                                    ? `'${searchTerm}'에 대한 검색 결과가 없습니다.`
                                    : `${currentYear}년 ${this.getMonthName(currentMonth)}에 등록된 주보가 없습니다.`
                            }
                        />
                    )}

                    {weeklyBulletins.length > 0 && (
                        <div className="weekly-latest-section">
                            <h3>최신 주보</h3>
                            <div className="latest-weekly-item">
                                <div className="latest-thumbnail">
                                    <img
                                        src={this.extractFirstImage(weeklyBulletins[0].contents) || "/placeholder.svg?height=200&width=300"}
                                        alt={weeklyBulletins[0].title}
                                        onError={(e) => {
                                            e.target.src = "/placeholder.svg?height=200&width=300"
                                        }}
                                    />
                                </div>
                                <div className="latest-info">
                                    <h4>{weeklyBulletins[0].title}</h4>
                                    <p>
                                        이번 주 예배 순서와 교회 소식을 확인하세요. 주보에는 예배 순서, 교회 소식, 기도 제목, 말씀 요약 등이
                                        포함되어 있습니다.
                                    </p>
                                    <div className="latest-date">
                                        <i className="far fa-calendar-alt"></i>
                                        <span>{weeklyBulletins[0].createdAt || weeklyBulletins[0].createdDate}</span>
                                    </div>
                                    <button onClick={() => this.handleWeeklyClick(weeklyBulletins[0], 0)} className="download-button">
                                        <i className="fas fa-eye"></i>
                                        <span>최신 주보 보기</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* 상세 정보 로딩 중 오버레이 */}
                {detailLoading && this.state.selectedWeeklyId && (
                    <div className="weekly-modal-overlay">
                        <BoardLoading title="주보 상세 정보를 불러오는 중..." subtitle="잠시만 기다려주세요" size="small" />
                    </div>
                )}

                {/* 주보 상세 모달 */}
                {this.state.showModal && this.state.selectedWeekly && (
                    <div className="weekly-modal-overlay" onClick={this.closeModal}>
                        <div className="weekly-modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="weekly-modal-header">
                                <h3>{this.state.selectedWeekly.title}</h3>
                                <div className="modal-header-actions">
                                    {/* 관리자만 수정/삭제 버튼 표시 */}
                                    {this.hasAdminPermission() && (
                                        <div className="modal-admin-actions">
                                            <button className="modal-edit-btn" onClick={this.handleModalEdit} title="수정">
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button className="modal-delete-btn" onClick={this.handleModalDelete} title="삭제">
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    )}
                                    <button className="modal-close-btn" onClick={this.closeModal}>
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            </div>
                            <div className="weekly-modal-body">
                                <div className="weekly-modal-date">
                                    <i className="far fa-calendar-alt"></i>
                                    <span>{this.state.selectedWeekly.createdAt || this.state.selectedWeekly.createdDate}</span>
                                </div>
                                <div className="weekly-modal-images">
                                    {this.extractAllImages(this.state.selectedWeekly.contents).map((imgSrc, index) => {
                                        const totalImages = this.extractAllImages(this.state.selectedWeekly.contents).length
                                        return (
                                            <div key={index} className="weekly-modal-image">
                                                <div className="image-label">
                                                    <span className="page-indicator">{this.getPageLabel(index, totalImages)}</span>
                                                    <button className="expand-btn" onClick={() => this.openImageModal(index)} title="이미지 확대">
                                                        <i className="fas fa-expand"></i>
                                                    </button>
                                                </div>
                                                <img
                                                    src={imgSrc || "/placeholder.svg"}
                                                    alt={`${this.state.selectedWeekly.title} - ${this.getPageLabel(index, totalImages)}`}
                                                    onClick={() => this.openImageModal(index)}
                                                    style={{ cursor: "pointer" }}
                                                />
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                            <div className="weekly-modal-footer">
                                <button className="download-button" onClick={() => this.downloadWeekly(this.state.selectedWeekly)}>
                                    <i className="fas fa-download"></i>
                                    <span>다운로드</span>
                                </button>
                                <button className="close-button" onClick={this.closeModal}>
                                    <i className="fas fa-times"></i>
                                    <span>닫기</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 이미지 확대 모달 */}
                {this.state.showImageModal && this.state.selectedWeekly && (
                    <div className="image-modal-overlay" onClick={this.closeImageModal}>
                        <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="image-modal-header">
                                <div className="image-info">
                                    <h4>{this.state.selectedWeekly.title}</h4>
                                    <span className="image-counter">
                    {this.getPageLabel(
                        this.state.selectedImageIndex,
                        this.extractAllImages(this.state.selectedWeekly.contents).length,
                    )}{" "}
                                        ({this.state.selectedImageIndex + 1} /{" "}
                                        {this.extractAllImages(this.state.selectedWeekly.contents).length})
                  </span>
                                </div>
                                <div className="image-controls">
                                    <button onClick={this.zoomOut} title="축소" disabled={this.state.imageZoom <= 0.5}>
                                        <i className="fas fa-search-minus"></i>
                                    </button>
                                    <span className="zoom-level">{Math.round(this.state.imageZoom * 100)}%</span>
                                    <button onClick={this.zoomIn} title="확대" disabled={this.state.imageZoom >= 3}>
                                        <i className="fas fa-search-plus"></i>
                                    </button>
                                    <button onClick={this.resetZoom} title="원본 크기">
                                        <i className="fas fa-compress"></i>
                                    </button>
                                    <button onClick={this.closeImageModal} title="닫기">
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            </div>
                            <div className="image-modal-body">
                                <button
                                    className="nav-btn prev-btn"
                                    onClick={this.previousImage}
                                    disabled={this.extractAllImages(this.state.selectedWeekly.contents).length <= 1}
                                >
                                    <i className="fas fa-chevron-left"></i>
                                </button>
                                <div className="image-container">
                                    <img
                                        src={
                                            this.extractAllImages(this.state.selectedWeekly.contents)[this.state.selectedImageIndex] ||
                                            "/placeholder.svg" ||
                                            "/placeholder.svg"
                                        }
                                        alt={`${this.state.selectedWeekly.title} - ${this.getPageLabel(
                                            this.state.selectedImageIndex,
                                            this.extractAllImages(this.state.selectedWeekly.contents).length,
                                        )}`}
                                        style={{
                                            transform: `scale(${this.state.imageZoom})`,
                                            transition: "transform 0.2s ease",
                                        }}
                                    />
                                </div>
                                <button
                                    className="nav-btn next-btn"
                                    onClick={this.nextImage}
                                    disabled={this.extractAllImages(this.state.selectedWeekly.contents).length <= 1}
                                >
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <style jsx>{`
          .weekly-item-wrapper {
            position: relative;
          }

          .weekly-item-actions {
            position: absolute;
            top: 10px;
            right: 10px;
            z-index: 10;
          }

          .action-btn {
            background: rgba(255, 255, 255, 0.9);
            border: none;
            cursor: pointer;
            padding: 8px;
            border-radius: 50%;
            color: #666;
            transition: all 0.2s;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .action-btn:hover {
            background: white;
            color: #333;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          }

          .dropdown-menu {
            position: absolute;
            top: 100%;
            right: 0;
            background: white;
            border: 1px solid #ddd;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            z-index: 1000;
            min-width: 120px;
          }

          .dropdown-item {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            padding: 12px 16px;
            background: none;
            border: none;
            text-align: left;
            cursor: pointer;
            font-size: 14px;
            color: #333;
            transition: background-color 0.2s;
          }

          .dropdown-item:hover {
            background-color: #f8f9fa;
          }

          .dropdown-item.edit {
            color: #007bff;
          }

          .dropdown-item.delete {
            color: #dc3545;
          }

          .dropdown-item.delete:hover {
            background-color: #fff5f5;
          }

          .weekly-modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px;
            border-bottom: 1px solid #eee;
          }

          .modal-header-actions {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .modal-admin-actions {
            display: flex;
            gap: 5px;
            margin-right: 10px;
          }

          .modal-edit-btn,
          .modal-delete-btn {
            background: none;
            border: 1px solid transparent;
            cursor: pointer;
            padding: 8px 12px;
            border-radius: 6px;
            transition: all 0.2s;
            font-size: 14px;
          }

          .modal-edit-btn {
            color: #007bff;
            border-color: #007bff;
          }

          .modal-edit-btn:hover {
            background-color: #007bff;
            color: white;
          }

          .modal-delete-btn {
            color: #dc3545;
            border-color: #dc3545;
          }

          .modal-delete-btn:hover {
            background-color: #dc3545;
            color: white;
          }

          .modal-close-btn {
            background: none;
            border: none;
            cursor: pointer;
            padding: 8px;
            color: #666;
            font-size: 18px;
          }

          .modal-close-btn:hover {
            color: #333;
          }
        `}</style>
            </section>
        )
    }

    // contents에서 첫 번째 이미지 URL 추출
    extractFirstImage = (contents) => {
        if (!contents) return null

        const imgMatch = contents.match(/<img[^>]+src="([^"]+)"/i)
        return imgMatch ? imgMatch[1] : null
    }

    // contents에서 모든 이미지 URL 추출
    extractAllImages = (contents) => {
        if (!contents) return []

        const imgMatches = contents.match(/<img[^>]+src="([^"]+)"/gi)
        if (!imgMatches) return []

        return imgMatches
            .map((match) => {
                const srcMatch = match.match(/src="([^"]+)"/)
                return srcMatch ? srcMatch[1] : null
            })
            .filter(Boolean)
    }
}

const mapStateToProps = (state) => ({
    board: state.board,
    loginData: state.login,
})

export default connect(mapStateToProps)(Weekly)
